import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import moment from 'moment';

import useNotification from '../../../core/hooks/use_notification';
import { getSalesTimeSeriesAction, getSalesTimeSeriesState } from '../../../features/stats/get_sales_time_series';
import { getSalesStatsNotificationData } from '../../../core/utils/notification_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../core/constants';
import { formatCompactCurrency, formatCompactNumber, formatCurrency, formatNumber, GranularityEnum, SalesChannelEnum, useChartColors } from './sales_analysis_utils';
import ChartCard from './chart_card';

const MetricEnum = {
    REVENUE: { value: 'revenue', label: 'Obrót', format: formatCurrency, axisFormat: formatCompactCurrency },
    ORDERS: { value: 'orders', label: 'Transakcje', format: formatNumber, axisFormat: formatCompactNumber }
};

const periodStartUnit = {
    [GranularityEnum.DAY.value]: 'day',
    [GranularityEnum.WEEK.value]: 'isoWeek',
    [GranularityEnum.MONTH.value]: 'month'
};

const periodLabel = (period, granularity) => {
    if (granularity === GranularityEnum.MONTH.value) return period.format('MM.YYYY');
    if (granularity === GranularityEnum.WEEK.value) return `od ${ period.format('DD.MM') }`;
    return period.format('DD.MM');
};

// every period of the range is present, so days without sales are shown as zero instead of disappearing
const buildDataset = (rows, query, granularity) => {
    const unit = periodStartUnit[granularity];
    const step = unit === 'isoWeek' ? 'week' : unit;
    const dataset = [];

    for (let period = moment(query.from).startOf(unit); period.isSameOrBefore(moment(query.to)); period.add(1, step)) {
        const key = period.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);
        const periodRows = rows?.filter(row => row?.period === key) || [];
        const value = (channel, field) => Number(periodRows.find(row => row?.channel === channel)?.[field] || 0);

        dataset.push({
            label: periodLabel(period, granularity),
            podRevenue: value(SalesChannelEnum.POD.value, 'revenue'),
            reservationRevenue: value(SalesChannelEnum.RESERVATION.value, 'revenue'),
            podOrders: value(SalesChannelEnum.POD.value, 'orders'),
            reservationOrders: value(SalesChannelEnum.RESERVATION.value, 'orders')
        });
    }

    return dataset;
};

const SalesChart = ({ query }) => {
    const chartColors = useChartColors();
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();
    const getSalesTimeSeries = useSelector(getSalesTimeSeriesState);

    const [granularity, setGranularity] = useState(GranularityEnum.DAY.value);
    const [metric, setMetric] = useState(MetricEnum.REVENUE.value);

    useEffect(() => {
        dispatch(getSalesTimeSeriesAction({ ...query, granularity }))
            .then(response => showErrorMessage(getSalesStatsNotificationData(response)));
    }, [dispatch, query, granularity, showErrorMessage]);

    const rows = getSalesTimeSeries?.data;
    const dataset = useMemo(() => buildDataset(rows, query, granularity), [rows, query, granularity]);
    const hasReservations = rows?.some(row => row?.channel === SalesChannelEnum.RESERVATION.value);
    const { format, axisFormat } = Object.values(MetricEnum).find(item => item.value === metric);
    const field = metric === MetricEnum.REVENUE.value ? 'Revenue' : 'Orders';

    const series = [
        { dataKey: `pod${ field }`, label: SalesChannelEnum.POD.label, stack: 'total', color: chartColors.primary, valueFormatter: format },
        ...(hasReservations ? [{ dataKey: `reservation${ field }`, label: SalesChannelEnum.RESERVATION.label, stack: 'total', color: chartColors.secondary, valueFormatter: format }] : [])
    ];

    return (
        <ChartCard
            title="Sprzedaż w czasie"
            subtitle={ hasReservations ? 'Sprzedaż stacjonarna i rezerwacje' : 'Sprzedaż stacjonarna' }
            loading={ getSalesTimeSeries?.loading }
            empty={ rows?.length === 0 }
            actions={
                <Stack direction="row" gap={ 1 } flexWrap="wrap">
                    <ToggleButtonGroup size="small" exclusive value={ metric } onChange={ (_, value) => value && setMetric(value) }>
                        { Object.values(MetricEnum).map(item => <ToggleButton key={ item.value } value={ item.value }>{ item.label }</ToggleButton>) }
                    </ToggleButtonGroup>
                    <ToggleButtonGroup size="small" exclusive value={ granularity } onChange={ (_, value) => value && setGranularity(value) }>
                        { Object.values(GranularityEnum).map(item => <ToggleButton key={ item.value } value={ item.value }>{ item.label }</ToggleButton>) }
                    </ToggleButtonGroup>
                </Stack>
            }
        >
            {
                rows && (
                    <BarChart
                        dataset={ dataset }
                        series={ series }
                        xAxis={ [{ scaleType: 'band', dataKey: 'label', categoryGapRatio: 0.3 }] }
                        yAxis={ [{ valueFormatter: axisFormat }] }
                        grid={ { horizontal: true } }
                        borderRadius={ 4 }
                        margin={ { left: 72, right: 16, top: hasReservations ? 48 : 16, bottom: 32 } }
                        slotProps={ { legend: { hidden: !hasReservations, position: { vertical: 'top', horizontal: 'right' } } } }
                    />
                )
            }
        </ChartCard>
    );
};

export default SalesChart;
